// Considered: DialogWrapper + ad-hoc content — rejected: it *is* DialogWrapper; this organism
// exists to own the diff + dependents table composition and the block/allow state, which three
// call sites (composer, Explorer, MCP-driven preview) share. See specs/space-conformance.md §
// Design System Reuse Audit.
import React from 'react';
import { useTranslation } from 'localization';
import type { ConformanceFinding, CompositionInterfaceChange } from '@center-platform/space-format/conformance';
import DialogWrapper from '../../molecules/DialogWrapper/DialogWrapper';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import ChipAtom from '../../atoms/Chip/ChipAtom';
import ConformanceFindingsListMolecule from '../../molecules/ConformanceFindingsList/ConformanceFindingsListMolecule';
import './PublishImpactDialogOrganism.scss';

/** A preflight/publish-gate dependent status — always freshly computed, never `stale` (that status only exists on stored sweep verdicts). */
export type PublishImpactDependentStatus = 'pass' | 'fail' | 'warn';

export interface PublishImpactDependent {
  artifactType: 'metaspace' | 'composition';
  artifactId: string;
  rebasePolicy: 'auto' | 'pinned';
  status: PublishImpactDependentStatus;
  findings: ConformanceFinding[];
}

export interface PublishImpactDialogOrganismProps {
  visible: boolean;
  onHide: () => void;
  breaking: CompositionInterfaceChange[];
  additive: CompositionInterfaceChange[];
  /** Visible (post-redaction) dependents only — the caller renders exactly what the route returned, no client-side filtering. */
  dependents: PublishImpactDependent[];
  /** Whether any dependent was redacted because the caller cannot `view` it — presence only, never a count. */
  hiddenDependents: boolean;
  /** Whether any hidden dependent's preflight status is `fail` — presence only, never enumerated or counted. */
  hiddenFailingDependents: boolean;
  wouldBlock: boolean;
  onPublish: () => void;
  publishing?: boolean;
  /**
   * A publish attempt that failed for a reason the gate itself didn't decide (network, 5xx, a 409
   * with no `impact` body). Rendered as an alert so the caller never sees a confirm click do
   * nothing; absent means no attempt has failed since the dialog opened.
   */
  errorMessage?: string;
  className?: string;
  'data-testid'?: string;
}

/** A prop or slot added as required reads differently from an optional one, and only the differ knows which — so it gets its own key rather than a conditional clause a translator can't reorder. */
function changeKeyOf(change: CompositionInterfaceChange): string {
  if (change.required && (change.type === 'PROP_ADDED' || change.type === 'SLOT_ADDED')) return `${change.type}_REQUIRED`;
  return change.type;
}

const PublishImpactDialogOrganism: React.FC<PublishImpactDialogOrganismProps> = ({
  visible,
  onHide,
  breaking,
  additive,
  dependents,
  hiddenDependents,
  hiddenFailingDependents,
  wouldBlock,
  onPublish,
  publishing = false,
  errorMessage,
  className = '',
  'data-testid': testId,
}) => {
  const { t } = useTranslation();

  if (!visible) return null;

  // `type` is the stable contract and the differ carries every specific structurally, so the change
  // renders as a localized sentence; `message` — the differ's own English prose — is only the
  // defaultValue for a type no bundle has a key for yet.
  const changeLabel = (change: CompositionInterfaceChange) =>
    t(`composer.publishImpact.change.${changeKeyOf(change)}`, {
      defaultValue: change.message,
      name: change.propName ?? change.slotName ?? change.emitName ?? '',
      from: change.fromType ?? change.fromKind ?? '',
      to: change.toType ?? change.toKind ?? '',
      removed: (change.removedEnumValues ?? []).join(', '),
    });

  const renderChanges = (changes: CompositionInterfaceChange[]) => (
    <ul className="publish-impact-dialog__change-list">
      {changes.map((change, index) => (
        <li key={`${change.type}:${index}`}>{changeLabel(change)}</li>
      ))}
    </ul>
  );

  return (
    <DialogWrapper
      header={t('composer.publishImpact.header', { defaultValue: 'Publish impact' })}
      visible={visible}
      onHide={onHide}
      className={`publish-impact-dialog ${className}`.trim()}
      data-testid={testId}
    >
      <div className="publish-impact-dialog__body">
        {wouldBlock && (
          <div className="publish-impact-dialog__block-message" role="alert" data-testid="publish-impact-dialog-block-message">
            {t('composer.publishImpact.wouldBlockMessage', {
              defaultValue: 'Publishing is blocked until every failing dependent is resolved.',
            })}
          </div>
        )}

        <section className="publish-impact-dialog__section" data-testid="publish-impact-dialog-breaking">
          <h3 className="publish-impact-dialog__section-heading">
            {t('composer.publishImpact.breakingHeading', { defaultValue: 'Breaking changes' })}
          </h3>
          {breaking.length === 0 ? (
            <p className="publish-impact-dialog__empty">
              {t('composer.publishImpact.noBreakingChanges', { defaultValue: 'No breaking changes.' })}
            </p>
          ) : (
            renderChanges(breaking)
          )}
        </section>

        <section className="publish-impact-dialog__section" data-testid="publish-impact-dialog-additive">
          <h3 className="publish-impact-dialog__section-heading">
            {t('composer.publishImpact.additiveHeading', { defaultValue: 'Additive changes' })}
          </h3>
          {additive.length === 0 ? (
            <p className="publish-impact-dialog__empty">
              {t('composer.publishImpact.noAdditiveChanges', { defaultValue: 'No additive changes.' })}
            </p>
          ) : (
            renderChanges(additive)
          )}
        </section>

        <section className="publish-impact-dialog__section" data-testid="publish-impact-dialog-dependents">
          <h3 className="publish-impact-dialog__section-heading">
            {t('composer.publishImpact.dependentsHeading', { defaultValue: 'Dependents' })}
          </h3>
          {dependents.length === 0 && !hiddenDependents ? (
            <p className="publish-impact-dialog__empty">
              {t('composer.publishImpact.noDependents', { defaultValue: 'No dependents.' })}
            </p>
          ) : (
            <div className="publish-impact-dialog__dependents-table" data-testid="publish-impact-dialog-dependents-table">
              <ul className="publish-impact-dialog__dependent-list">
              {dependents.map((dependent) => (
                <li
                  className="publish-impact-dialog__dependent-row"
                  key={`${dependent.artifactType}:${dependent.artifactId}`}
                  data-testid={`publish-impact-dialog-dependent-${dependent.artifactId}`}
                >
                  <span className="publish-impact-dialog__dependent-id">{dependent.artifactId}</span>
                  <ChipAtom label={dependent.artifactType} />
                  <ChipAtom label={t(`composer.publishImpact.rebasePolicy.${dependent.rebasePolicy}`, { defaultValue: dependent.rebasePolicy })} />
                  <ChipAtom label={t(`composer.publishImpact.status.${dependent.status}`, { defaultValue: dependent.status })} />
                  {dependent.findings.length > 0 && (
                    <ConformanceFindingsListMolecule findings={dependent.findings} />
                  )}
                </li>
              ))}
              </ul>
              {hiddenDependents && (
                <p className="publish-impact-dialog__dependent-row publish-impact-dialog__dependent-row--hidden">
                  {t('composer.publishImpact.hiddenDependents', {
                    defaultValue: 'Some dependents are hidden because you do not have access to view them.',
                  })}
                </p>
              )}
              {hiddenFailingDependents && (
                <p className="publish-impact-dialog__dependent-row publish-impact-dialog__dependent-row--hidden-failing">
                  {t('composer.publishImpact.hiddenFailingDependents', {
                    defaultValue: 'At least one hidden dependent is currently failing.',
                  })}
                </p>
              )}
            </div>
          )}
        </section>

        {errorMessage && (
          <div
            className="publish-impact-dialog__error-message"
            role="alert"
            data-testid="publish-impact-dialog-error-message"
          >
            {errorMessage}
          </div>
        )}

        <div className="publish-impact-dialog__actions">
          <ButtonAtom
            label={t('composer.publishImpact.cancel', { defaultValue: 'Cancel' })}
            variant="text"
            onClick={onHide}
            disabled={publishing}
          />
          <ButtonAtom
            label={t('composer.publishImpact.publish', { defaultValue: 'Publish' })}
            onClick={onPublish}
            disabled={wouldBlock || publishing}
          />
        </div>
      </div>
    </DialogWrapper>
  );
};

PublishImpactDialogOrganism.displayName = 'PublishImpactDialogOrganism';

export default PublishImpactDialogOrganism;
